<template>
  <div class="flex items-center gap-2">
    <Badge :variant="statusMeta.variant" class="h-7 rounded-full px-3 capitalize" :class="statusMeta.class">
      <component :is="statusMeta.icon" class="h-3.5 w-3.5" />
      <span v-if="showLabel" class="font-medium">
        Status:
      </span> <span>{{ statusMeta.label }}</span>
    </Badge>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Circle, Check, Clock3, X } from 'lucide-vue-next'
import { Badge } from '../../../../../../../../resources/js/components/ui/badge/index.js'

const STATUS_META_MAP = {
  draft: { label: 'Draft', icon: Circle, variant: 'outline', class: 'border-transparent bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900/70 dark:text-slate-200 dark:ring-slate-700' },
  open: { label: 'Open', icon: Circle, variant: 'outline', class: 'border-transparent bg-sky-50 text-sky-700 ring-1 ring-sky-200 dark:bg-sky-950/50 dark:text-sky-200 dark:ring-sky-800' },
  assigned: { label: 'Assigned', icon: Circle, variant: 'outline', class: 'border-transparent bg-violet-50 text-violet-700 ring-1 ring-violet-200 dark:bg-violet-950/50 dark:text-violet-200 dark:ring-violet-800' },
  accepted: { label: 'Accepted', icon: Check, variant: 'outline', class: 'border-transparent bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-200 dark:ring-emerald-800' },
  in_progress: { label: 'In Progress', icon: Clock3, variant: 'outline', class: 'border-transparent bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-950/50 dark:text-blue-200 dark:ring-blue-800' },
  blocked: { label: 'Blocked', icon: X, variant: 'outline', class: 'border-transparent bg-amber-50 text-amber-800 ring-1 ring-amber-200 dark:bg-amber-950/50 dark:text-amber-200 dark:ring-amber-800' },
  pending_approval: { label: 'Pending Approval', icon: Circle, variant: 'outline', class: 'border-transparent bg-orange-50 text-orange-800 ring-1 ring-orange-200 dark:bg-orange-950/50 dark:text-orange-200 dark:ring-orange-800' },
  review_rejected: { label: 'Review Rejected', icon: X, variant: 'outline', class: 'border-transparent bg-rose-50 text-rose-700 ring-1 ring-rose-200 dark:bg-rose-950/50 dark:text-rose-200 dark:ring-rose-800' },
  completed: { label: 'Completed', icon: Check, variant: 'outline', class: 'border-transparent bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-200 dark:ring-emerald-800' },
  cancelled: { label: 'Cancelled', icon: X, variant: 'outline', class: 'border-transparent bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900/70 dark:text-slate-300 dark:ring-slate-700' },
}

const props = defineProps({
  value: {
    type: [String, Object],
    default: null,
  },
  showLabel: {
    type: Boolean,
    default: false,
  },
})

const normalizedStatus = computed(() => {
  if (typeof props.value === 'string') {
    return props.value
  }

  if (props.value && typeof props.value === 'object' && 'value' in props.value) {
    return String(props.value.value)
  }

  return null
})

const statusMeta = computed(() => {
  if (normalizedStatus.value && STATUS_META_MAP[normalizedStatus.value]) {
    return STATUS_META_MAP[normalizedStatus.value]
  }

  return {
    label: 'Unknown',
    icon: Circle,
    variant: 'outline',
    class: 'border-transparent bg-muted text-muted-foreground ring-1 ring-border',
  }
})
</script>
